import { SHOT_LANDING_LABELS, SHOT_LANDINGS } from '@golf/common';
import type { ShotLanding } from '@golf/common';
import { Flag, Sprout, TreePine, Waves, Wind, XOctagon } from 'lucide-react';
import type { ReactNode } from 'react';

interface Props {
    /** Le joueur estime où sa balle vient de tomber. Le détail — pénalité,
     *  correction, balle rentrée — se règle sur le bilan qui suit. */
    onPick: (landing: ShotLanding) => void | Promise<void>;
    disabled?: boolean;
}

/** L'aplat, l'encre et le bord d'une zone.
 *
 *  `backgroundColor` et non `background` : la MATIÈRE vient d'une classe
 *  `texture-*`, qui pose son motif en `background-image`. La couleur d'un côté,
 *  le grain de l'autre, et les deux se superposent sans se marcher dessus.
 *
 *  Le bord se dérive de l'aplat plutôt que d'avoir son jeton : six teintes de
 *  bord à tenir d'accord avec six fonds, c'étaient six occasions de dériver
 *  pour rien. */
export function habitZone(zone: ShotLanding): {
    backgroundColor: string; color: string; border: string;
} {
    return {
        backgroundColor: `var(--zone-${zone})`,
        color: `var(--zone-${zone}-encre)`,
        border: `2px solid color-mix(in oklab, var(--zone-${zone}) 72%, black)`,
    };
}

export const ICONES_ZONE: Record<ShotLanding, ReactNode> = {
    fairway: <Sprout size={22} aria-hidden="true" />,
    green: <Flag size={22} aria-hidden="true" />,
    rough: <TreePine size={22} aria-hidden="true" />,
    bunker: <Wind size={22} aria-hidden="true" />,
    penalty: <Waves size={22} aria-hidden="true" />,
    out_of_bounds: <XOctagon size={22} aria-hidden="true" />,
};

/* Le pavé de saisie du parcours : OÙ la balle est tombée, à l'estime.
 *
 * ── Pourquoi la zone et non le contact ──────────────────────────────────────
 *
 * L'écran demandait la qualité du contact — centré, slice, gratte, socket. Au
 * practice c'est la bonne question : on tape cinquante balles pour voir
 * laquelle revient, et le geste est l'objet de la séance. Sur le parcours on
 * joue une balle et on marche jusqu'à elle ; ce qui décide de la suite n'est
 * pas la façon dont on l'a frappée mais l'endroit où elle repose. Le club se
 * choisit sur la zone, la pénalité se compte sur la zone, et fairway en
 * régulation, green en régulation et scrambling s'en déduisent — aucun de ces
 * trois ne sort d'un « slice ».
 *
 * ── Six boutons et rien d'autre ─────────────────────────────────────────────
 *
 * Le pavé a porté un temps les pénalités : un « +1 » sur l'eau, une bascule
 * « balle injouable », une seconde cible pour la balle jouée dans l'obstacle.
 * Cela faisait quatre décisions à prendre debout, la balle encore en l'air, et
 * le joueur les prenait mal — il faut avoir marché jusqu'à sa balle pour
 * savoir si elle est injouable.
 *
 * Ici on ESTIME, d'un appui : « je crois qu'elle est dans le rough ». Le bilan
 * qui suit montre ce coup en grand et laisse tout corriger — la zone, la
 * pénalité, et le cas où la balle est simplement tombée dedans. C'est le
 * moment où l'on est arrivé près d'elle, et où l'on sait.
 *
 * ── Trois rangs, du meilleur au pire ────────────────────────────────────────
 *
 * Fairway et green en haut, en grand : ce sont les deux issues qu'on espère et
 * les plus fréquentes. Rough et bunker au milieu : gênant, pas grave. Les deux
 * zones qui coûtent en bas — assez loin du pouce pour qu'on ne les touche pas
 * par accident, assez visibles pour se trouver sans chercher.
 */
export function PaveZone({ onPick, disabled = false }: Props) {
    /* Chaque bouton porte la MATIÈRE de sa zone, la même qu'au bilan : le grain
     * du green tondu à ras, le sable irrégulier du bunker, les brins du rough,
     * la houle de l'eau. Le joueur touche le sable, puis se retrouve devant un
     * plein écran de sable — c'est la même chose qui continue, pas deux écrans
     * qui se ressemblent.
     *
     * Elle sert aussi à distinguer : green et bunker sont les deux zones
     * claires, et à côté l'une de l'autre sous un écran au soleil, la teinte
     * seule ne suffit pas. */
    const Zone = ({ zone, grand = false }: { zone: ShotLanding; grand?: boolean }) => (
        <button
            type="button"
            disabled={disabled}
            onClick={() => void onPick(zone)}
            style={habitZone(zone)}
            className={`texture-${zone} flex items-center justify-center gap-2 rounded-field
                        font-bold shadow-sm transition-transform active:scale-[0.98]
                        disabled:opacity-40 ${
                grand ? 'min-h-0 flex-1 text-xl' : 'min-h-14 text-base'}`}
        >
            {ICONES_ZONE[zone]}
            {SHOT_LANDING_LABELS[zone]}
        </button>
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
            {/* Les deux issues qu'on espère, à parts égales : sur un par 3 on
                vise le green, sur un par 4 le fairway, et aucune des deux n'est
                plus fréquente que l'autre sur dix-huit trous. */}
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-2">
                <Zone zone="fairway" grand />
                <Zone zone="green" grand />
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-2">
                <Zone zone="rough" />
                <Zone zone="bunker" />
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-2">
                <Zone zone="penalty" />
                <Zone zone="out_of_bounds" />
            </div>
        </div>
    );
}

/** Les six zones, dans l'ordre de l'écran. Réexporté pour que le bilan propose
 *  la même liste dans le même ordre : deux ordres pour six mêmes boutons, et
 *  le joueur cherche. */
export const ZONES = SHOT_LANDINGS;
